import {applyWorkUpdate} from "src/app/modules/lists/utils/work-update.util";
import {WorkUpdate} from "src/app/modules/shared/interfaces/WorkUpdate.interface";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {CdkMenuModule} from "@angular/cdk/menu";
import {
  CatalogSearchSort,
  CATALOG_SEARCH_SORT_LABELS,
} from "src/app/enums/CatalogSearchSort.enum";
import {
  Subject,
  Subscription,
  debounceTime,
  forkJoin,
  Observable,
  of,
  tap,
} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {WorksTypes, WorksLabels} from "src/app/enums/Works.enum";
import {resolveWorkDisplayType} from "src/app/utils/work-display-type.util";
import {WorkEntityKind} from "src/app/enums/WorkEntityKind.enum";
import {WorksModel} from "src/app/models/WorksModel";
import {User} from "src/app/modules/auth/models/User";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {WorksService} from "src/app/modules/shared/services/works.service";
import {UserService} from "src/app/modules/shared/services/user.service";
import {ScreenSizeService} from "src/app/modules/shared/services/screenSize.service";
import {MediaPreferencesService} from "src/app/modules/shared/services/media-preferences.service";
import {
  CatalogSearchContext,
  CatalogSearchPage,
  CatalogSearchResponse,
} from "src/app/modules/shared/interfaces/CatalogSearch.interface";
import {WorkEntitySuggestion} from "src/app/modules/shared/interfaces/WorkEntitySuggestion.interface";
import {QueryFilters} from "src/app/modules/lists/interfaces/filter.interface";
import {EntityTagsComponent} from "src/app/modules/lists/components/entity-tags/entity-tags.component";
import {CatalogSearchResultsComponent} from "../catalog-search-results/catalog-search-results.component";
import {CatalogBetaHintComponent} from "src/app/modules/shared/components/catalog-beta-hint/catalog-beta-hint.component";
import {PointerHoverDirective} from "src/app/modules/shared/directives/pointer-hover.directive";
import {ModalService} from "../../services/modal.service";
import {WorkModalNavigationContext} from "src/app/modules/shared/interfaces/work-modal-navigation.interface";
import {WorkModalNavigationService} from "../../services/work-modal-navigation.service";
import {WorksListsService} from "src/app/modules/lists/services/works-lists.service";
import {isCatalogBetaWorkType} from "src/app/modules/shared/constants/catalog-beta-work-types.constants";
import {getEntitySuggestionKey} from "src/app/utils/entity-tag.util";
import {getCatalogSearchPresentation} from "src/app/utils/catalog-search-presentation.util";
import {getEntityRoleLabelKey} from "src/app/utils/entity-presentation.util";
import {buildCatalogSuggestionGroups} from "src/app/utils/catalog-suggestion-groups.util";
import {getCatalogSearchFilters} from "src/app/utils/catalog-search-context.util";
import {getHorizontalOverflowMask} from "src/app/utils/overflow-mask.util";
import {CatalogTrendingComponent} from "../catalog-trending/catalog-trending.component";
import {CatalogSearchHistoryComponent} from "../catalog-search-history/catalog-search-history.component";

@Component({
  selector: "app-add-work",
  templateUrl: "./add-work.component.html",
  styleUrls: ["./add-work.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    NgClass,
    TranslateModule,
    EntityTagsComponent,
    CatalogSearchResultsComponent,
    CatalogBetaHintComponent,
    PointerHoverDirective,
    CatalogTrendingComponent,
    CatalogSearchHistoryComponent,
    CdkMenuModule,
  ],
})
export class AddWorkComponent implements OnInit, OnDestroy {
  initialSearch: CatalogSearchContext = {query: "", filters: {}, entities: []};
  @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild("mediaFilters") mediaFilters?: ElementRef<HTMLDivElement>;
  @ViewChild("searchHeading") searchHeading?: ElementRef<HTMLElement>;
  readonly isCatalogBetaWorkType = isCatalogBetaWorkType;
  readonly getEntitySuggestionKey = getEntitySuggestionKey;
  readonly getEntityRoleLabelKey = getEntityRoleLabelKey;
  readonly getHorizontalOverflowMask = getHorizontalOverflowMask;
  readonly searchForm: FormGroup;
  user?: User;
  readonly worksLabels = WorksLabels;
  searchTerm = "";
  displayedSearchTerm = "";
  searchType: WorksTypes | null = null;
  libraryOnly = false;
  searchSort = CatalogSearchSort.Relevance;
  readonly searchSortOptions = Object.values(CatalogSearchSort);
  readonly searchSortLabels = CATALOG_SEARCH_SORT_LABELS;
  worksTypesArray: WorksTypes[] = [];
  works: WorksModel[] = [];
  selectedEntities: WorkEntitySuggestion[] = [];
  entitySuggestions: WorkEntitySuggestion[] = [];
  collectionSuggestions: WorkEntitySuggestion[] = [];
  userResearch: string[] = [];
  trendingRevision = 0;
  isLoading = false;
  loadingMore = false;
  isSearchUnavailable = false;
  hasSearchError = false;
  isMobile = false;
  mediaOverflowStart = false;
  mediaOverflowEnd = false;
  stickyToolbarRaised = false;
  private headerObserver?: IntersectionObserver;
  count = 0;
  countIsExact = true;
  hasMore = false;
  private offset = 0;
  private filters: QueryFilters = {};
  private readonly inputTerm$ = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private requestSubscription?: Subscription;
  private hydrationSubscription?: Subscription;
  private queryBeforeEntitySelection = "";
  private textEditedInScope = false;
  private typeBeforeEntitySelection: WorksTypes | null = null;
  private pendingInputTerm: string | null = null;
  private readonly resultCache = new Map<string, CatalogSearchPage>();
  private readonly entityCache = new Map<string, WorkEntitySuggestion[]>();
  isOpen = false;
  private previousNavigationContext: WorkModalNavigationContext | null = null;
  private readonly workModalNavigationService = inject(
    WorkModalNavigationService
  );
  public readonly modalNavigationContext: WorkModalNavigationContext = {
    getWorks: () => this.visibleWorks,
    canLoadMore: () => false,
    loadMore: () => undefined,
    isFullyLoaded: () => false,
  };
  private readonly worksListsService = inject(WorksListsService);

  constructor(
    formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly worksService: WorksService,
    private readonly screenSizeService: ScreenSizeService,
    private readonly mediaPreferencesService: MediaPreferencesService,
    private readonly modalService: ModalService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.searchForm = formBuilder.group({search: [""]});
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.worksListsService.subscribeToWorkUpdates({
        apply: (update) => this.applyWorkUpdate(update),
        shouldRefresh: (update) =>
          this.isOpen &&
          this.libraryOnly &&
          !update.local &&
          ["add", "remove"].includes(update.operator),
        refresh: () => this.runSearch(),
      })
    );
    this.subscriptions.add(
      this.inputTerm$.pipe(debounceTime(500)).subscribe((term) => {
        if (this.isOpen && term === this.pendingInputTerm) this.runSearch(true);
      })
    );
    this.subscriptions.add(
      this.authService.user$.subscribe((user) => {
        const userChanged = user?._id !== this.user?._id;
        this.user = user ?? undefined;
        this.worksTypesArray = this.mediaPreferencesService.filterTypes(
          Object.values(WorksTypes),
          (type) => type
        );
        if (user && userChanged) {
          this.userResearch = [...(user.research ?? [])].reverse();
          if (this.isOpen) this.applyInitialSearch();
          this.loadSearchHistory();
        }
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.add(
      this.screenSizeService.screenSize$.subscribe((size) => {
        this.isMobile = size.isMobile;
        setTimeout(() => this.updateMediaEdges());
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.add(
      this.modalService.modalItem$.subscribe(({item}) => {
        if (item && "type" in item) this.mergeHydratedWork(item);
      })
    );
    this.subscriptions.add(
      this.modalService.modals$.subscribe((modals) => {
        const wasOpen = this.isOpen;
        this.isOpen = !!modals["addWork"];
        if (wasOpen && !this.isOpen) {
          this.cancelSearch();
          this.restoreNavigationContext();
        }
      })
    );
    this.subscriptions.add(
      this.modalService.modalOpened.subscribe((event) => {
        if (
          event?.modalId === "addWork" &&
          event.opened &&
          this.modalService.isModalOpen("addWork")
        ) {
          this.openSearch();
        }
      })
    );
    if (this.isOpen) this.openSearch();
  }

  private openSearch(): void {
    if (
      this.workModalNavigationService.currentContext !==
      this.modalNavigationContext
    ) {
      this.previousNavigationContext =
        this.workModalNavigationService.currentContext;
    }
    this.initialSearch = this.modalService.getCatalogSearchContext() ?? {
      query: "",
      filters: {},
      entities: [],
    };
    if (this.user) this.applyInitialSearch();
    setTimeout(() => this.focusSearch());
  }

  private focusSearch(): void {
    this.updateMediaEdges();
    this.observeSearchHeading();
    if (
      !this.initialSearch.entities?.length &&
      !/iPhone|iPad|iPod/.test(window.navigator.userAgent)
    ) {
      this.searchInput?.nativeElement.focus();
    }
  }

  private observeSearchHeading(): void {
    this.headerObserver?.disconnect();
    const heading = this.searchHeading?.nativeElement;
    if (!heading) return;
    this.headerObserver = new IntersectionObserver(
      ([entry]) => {
        this.stickyToolbarRaised = !entry.isIntersecting;
        this.cdr.detectChanges();
      },
      {root: heading.closest(".modal-container")}
    );
    this.headerObserver.observe(heading);
  }

  get visibleWorks(): WorksModel[] {
    return this.works.filter((work) => {
      const type = resolveWorkDisplayType(work);
      return (
        this.worksTypesArray.some((enabled) => enabled === type) &&
        (!this.searchType || type === this.searchType)
      );
    });
  }

  get hasSearch(): boolean {
    return (
      !!this.searchTerm ||
      this.libraryOnly ||
      this.selectedEntities.length > 0 ||
      Object.keys(this.filters).length > 0
    );
  }

  get peopleEntities(): WorkEntitySuggestion[] {
    if (!this.hasSearch || this.selectedEntities.length) return [];
    return buildCatalogSuggestionGroups(
      this.displayedSearchTerm,
      this.entitySuggestions,
      this.collectionSuggestions
    ).people;
  }

  get collectionEntities(): WorkEntitySuggestion[] {
    if (!this.hasSearch || this.selectedEntities.length) return [];
    return buildCatalogSuggestionGroups(
      this.displayedSearchTerm,
      this.entitySuggestions,
      this.collectionSuggestions
    ).collections;
  }

  get genreFilters(): string[] {
    return this.filters.genres ?? [];
  }

  get releaseYearFilters(): string[] {
    return (this.filters.releaseDate ?? []).map((date) => String(date.year));
  }

  get searchPresentation() {
    return getCatalogSearchPresentation(
      this.selectedEntities,
      Object.keys(this.filters).length > 0
    );
  }

  private applyInitialSearch(): void {
    this.cancelSearch();
    this.resultCache.clear();
    this.entityCache.clear();
    this.libraryOnly = this.initialSearch.libraryOnly === true;
    this.searchSort = CatalogSearchSort.Relevance;
    this.filters = {...this.initialSearch.filters};
    delete this.filters.query;
    delete this.filters.workType;
    delete this.filters.people;
    delete this.filters.companies;
    delete this.filters.series;
    delete this.filters.franchises;
    this.selectedEntities = [...this.initialSearch.entities];
    this.textEditedInScope = false;
    this.searchTerm = this.initialSearch.query;
    this.queryBeforeEntitySelection =
      this.searchTerm || this.selectedEntities[0]?.name || "";
    this.searchForm.setValue({search: this.searchTerm});
    const type = this.initialSearch.type;
    this.searchType =
      this.worksTypesArray.find((enabled) => enabled === String(type)) ?? null;
    this.typeBeforeEntitySelection = this.searchType;
    this.entitySuggestions = [];
    this.collectionSuggestions = [];
    this.hydrateSelectedEntities();
    this.runSearch();
  }

  private hydrateSelectedEntities(): void {
    this.hydrationSubscription?.unsubscribe();
    const userId = this.user?._id;
    if (!userId || !this.selectedEntities.length) return;
    const requests = this.selectedEntities
      .filter(
        (entity) =>
          entity.kind === WorkEntityKind.People ||
          entity.kind === WorkEntityKind.Company
      )
      .map((entity) => this.worksService.searchEntities(userId, entity.name));
    if (!requests.length) return;
    this.hydrationSubscription = forkJoin(requests).subscribe((results) => {
      const suggestions = results.flat();
      this.selectedEntities = this.selectedEntities.map(
        (entity) =>
          suggestions.find(
            (suggestion) =>
              suggestion.kind === entity.kind &&
              suggestion.tagId === entity.tagId
          ) ?? entity
      );
      this.cdr.markForCheck();
    });
  }

  onInputChange(event: Event): void {
    this.cancelSearch();
    const searchTerm = (event.target as HTMLInputElement).value.trim();
    if (!this.hasSearch || !searchTerm) this.works = [];
    if (this.selectedEntities.length) this.textEditedInScope = true;
    this.searchTerm = searchTerm;
    this.hasMore = false;
    this.isLoading = this.hasSearch;
    if (!this.hasSearch) return this.runSearch();
    this.pendingInputTerm = this.searchTerm;
    this.inputTerm$.next(this.searchTerm);
  }

  selectType(type: WorksTypes | null, event: Event): void {
    const button = event.currentTarget;
    const container = this.mediaFilters?.nativeElement;
    if (button instanceof HTMLElement && container) {
      const buttonRect = button.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      container.scrollTo({
        left:
          container.scrollLeft +
          buttonRect.left -
          containerRect.left -
          (containerRect.width - buttonRect.width) / 2,
        behavior: "smooth",
      });
    }
    if (this.searchType === type) return;
    this.searchType = type;
    this.runSearch();
  }

  toggleLibraryOnly(): void {
    this.libraryOnly = !this.libraryOnly;
    this.modalService.setCatalogSearchLibraryOnly(this.libraryOnly);
    this.runSearch();
  }

  changeSort(sort: CatalogSearchSort): void {
    if (sort === this.searchSort || !this.selectedEntities.length) return;
    this.searchSort = sort;
    this.runSearch();
  }

  updateMediaEdges(): void {
    const container = this.mediaFilters?.nativeElement;
    if (!container) return;
    this.mediaOverflowStart = container.scrollLeft > 1;
    this.mediaOverflowEnd =
      container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
    this.cdr.markForCheck();
  }

  searchAgain(term: string): void {
    this.clearFilters();
    this.searchTerm = term;
    this.searchForm.setValue({search: term});
    this.runSearch();
  }

  clearSearch(): void {
    if (this.selectedEntities.length) this.textEditedInScope = true;
    this.searchTerm = "";
    this.searchForm.setValue({search: ""});
    this.runSearch();
    this.searchInput?.nativeElement.focus();
  }

  closeSearch(): void {
    this.modalService.closeModal("addWork", true);
  }

  private clearFilters(): void {
    this.filters = {};
    this.selectedEntities = [];
    this.entitySuggestions = [];
    this.collectionSuggestions = [];
    this.searchType = null;
    this.queryBeforeEntitySelection = "";
    this.typeBeforeEntitySelection = null;
    this.textEditedInScope = false;
  }

  onEntityToggled(entity: WorkEntitySuggestion): void {
    const selected = this.selectedEntities.some(
      (item) => getEntitySuggestionKey(item) === getEntitySuggestionKey(entity)
    );
    if (!selected && !this.selectedEntities.length) {
      this.queryBeforeEntitySelection = this.searchTerm || entity.name;
      this.typeBeforeEntitySelection = this.searchType;
      this.textEditedInScope = false;
    }
    this.selectedEntities = selected
      ? this.selectedEntities.filter(
          (item) =>
            getEntitySuggestionKey(item) !== getEntitySuggestionKey(entity)
        )
      : [...this.selectedEntities, entity];
    if (!selected) {
      this.searchTerm = "";
    } else if (!this.selectedEntities.length) {
      if (!this.textEditedInScope)
        this.searchTerm = this.queryBeforeEntitySelection;
      this.searchType = this.typeBeforeEntitySelection;
    }
    this.applySelectedSeriesType(entity.kind === WorkEntityKind.Series);
    this.searchForm.setValue({search: this.searchTerm});
    this.runSearch();
  }

  private applySelectedSeriesType(seriesToggled: boolean): void {
    if (!seriesToggled) return;
    const series = this.selectedEntities.find(
      (entity) => entity.kind === WorkEntityKind.Series
    );
    const type = series?.type;
    this.searchType =
      this.worksTypesArray.find((enabled) => enabled === String(type)) ??
      this.typeBeforeEntitySelection;
  }

  removeGenre(genre: string): void {
    this.filters.genres = this.genreFilters.filter((value) => value !== genre);
    if (!this.filters.genres.length) delete this.filters.genres;
    this.runSearch();
  }

  removeReleaseYear(year: string): void {
    this.filters.releaseDate = this.filters.releaseDate?.filter(
      (date) => String(date.year) !== year
    );
    if (!this.filters.releaseDate?.length) delete this.filters.releaseDate;
    this.runSearch();
  }

  private searchFilters(): QueryFilters {
    return getCatalogSearchFilters(
      this.filters,
      this.selectedEntities,
      this.searchType,
      this.worksTypesArray
    );
  }

  get searchCacheKey(): string {
    return JSON.stringify([
      this.searchTerm,
      this.searchFilters(),
      this.libraryOnly,
      this.searchSort,
    ]);
  }

  runSearch(preserveResults = false): void {
    this.cancelSearch();
    if (!this.selectedEntities.length)
      this.searchSort = CatalogSearchSort.Relevance;
    if (!preserveResults || !this.hasSearch) {
      this.works = [];
      this.entitySuggestions = [];
      this.collectionSuggestions = [];
    }
    this.count = 0;
    this.countIsExact = true;
    this.offset = 0;
    this.hasMore = false;
    this.isSearchUnavailable = false;
    this.hasSearchError = false;
    this.notifyNavigationContextUpdated();
    if (!this.user || !this.isOpen || !this.hasSearch) {
      this.trendingRevision++;
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }
    const cached = this.resultCache.get(this.searchCacheKey);
    if (cached) {
      this.works = cached.works;
      this.count = cached.count;
      this.countIsExact = cached.countIsExact;
      this.offset = cached.offset;
      this.hasMore = cached.hasMore;
      this.collectionSuggestions = cached.collections;
      this.entitySuggestions = this.entityCache.get(this.searchTerm) ?? [];
      this.displayedSearchTerm = this.searchTerm;
      this.isLoading = false;
      this.notifyNavigationContextUpdated();
      this.cdr.markForCheck();
      return;
    }
    this.isLoading = true;
    this.requestSubscription = forkJoin({
      results: this.worksService.searchCatalog(
        this.user._id,
        this.searchTerm,
        this.searchFilters(),
        0,
        25,
        this.libraryOnly,
        this.searchSort
      ),
      entities: this.loadEntitySuggestions(),
    }).subscribe({
      next: ({results, entities}) => {
        this.entitySuggestions = entities;
        this.applyResults(results);
        this.isLoading = false;
        if (this.searchTerm)
          this.userResearch = [
            this.searchTerm,
            ...this.userResearch.filter((term) => term !== this.searchTerm),
          ];
        this.cdr.markForCheck();
      },
      error: (error: HttpErrorResponse) => this.onSearchError(error),
    });
  }

  private loadEntitySuggestions(): Observable<WorkEntitySuggestion[]> {
    if (
      !this.user ||
      this.searchTerm.length < 2 ||
      this.selectedEntities.length
    )
      return of([]);
    const term = this.searchTerm;
    const cached = this.entityCache.get(term);
    if (cached) return of(cached);
    return this.worksService.searchEntities(this.user._id, term).pipe(
      tap((entities) => {
        if (entities.length) this.entityCache.set(term, entities);
      })
    );
  }

  loadMore(): void {
    if (!this.user || this.loadingMore || !this.hasMore || this.isLoading)
      return;
    this.loadingMore = true;
    this.hasSearchError = false;
    this.isSearchUnavailable = false;
    this.requestSubscription = this.worksService
      .searchCatalog(
        this.user._id,
        this.searchTerm,
        this.searchFilters(),
        this.offset,
        25,
        this.libraryOnly,
        this.searchSort
      )
      .subscribe({
        next: (results) => {
          this.applyResults(results, true);
          this.loadingMore = false;
          this.cdr.markForCheck();
        },
        error: (error: HttpErrorResponse) => this.onSearchError(error),
      });
  }

  retrySearch(): void {
    if (
      this.hasMore &&
      this.works.length &&
      this.displayedSearchTerm === this.searchTerm
    )
      this.loadMore();
    else this.runSearch(true);
  }

  getWorkKey(work: WorksModel): string {
    return `${work.type}:${work._id}`;
  }

  private applyResults(results: CatalogSearchResponse, append = false): void {
    this.displayedSearchTerm = this.searchTerm;
    this.collectionSuggestions = results.collections.map((collection) => ({
      ...collection,
      tagId: collection.name,
      name: collection.label ?? collection.name,
      slug: "",
    }));
    const works = new Map(
      (append ? this.works : []).map((work) => [this.getWorkKey(work), work])
    );
    for (const work of results.data) works.set(this.getWorkKey(work), work);
    this.works = [...works.values()];
    this.count = results.pagination.count;
    this.countIsExact = results.pagination.countIsExact ?? this.count < 500;
    const nextOffset =
      results.pagination.nextOffset ??
      results.pagination.offset + results.pagination.limit;
    this.hasMore = results.pagination.hasMore && nextOffset > this.offset;
    this.offset = nextOffset;
    this.resultCache.set(this.searchCacheKey, {
      works: this.works,
      count: this.count,
      countIsExact: this.countIsExact,
      offset: this.offset,
      hasMore: this.hasMore,
      collections: this.collectionSuggestions,
    });
    this.notifyNavigationContextUpdated();
  }

  private onSearchError(error: HttpErrorResponse): void {
    this.isSearchUnavailable = error.status === 503;
    this.hasSearchError = true;
    this.isLoading = false;
    this.loadingMore = false;
    this.cdr.markForCheck();
  }

  private cancelSearch(): void {
    this.pendingInputTerm = null;
    this.requestSubscription?.unsubscribe();
    this.loadingMore = false;
  }

  private loadSearchHistory(): void {
    if (!this.user) return;
    this.subscriptions.add(
      this.userService.getUser(this.user._id, "research").subscribe({
        next: (user) => {
          this.userResearch = [...(user.research ?? [])].reverse();
          this.cdr.markForCheck();
        },
      })
    );
  }

  deleteSearchHistory(): void {
    if (!this.user) return;
    this.subscriptions.add(
      this.userService.deleteUserSearchHistory(this.user._id).subscribe({
        next: () => {
          this.userResearch = [];
          this.cdr.markForCheck();
        },
      })
    );
  }

  applyWorkUpdate(update: WorkUpdate): boolean {
    if (update.list) return false;
    this.resultCache.clear();
    const works = applyWorkUpdate(this.works, update, {
      catalog: !this.libraryOnly,
    });
    if (works !== this.works) {
      this.requestSubscription?.unsubscribe();
      this.hydrationSubscription?.unsubscribe();
      this.offset = Math.max(0, this.offset + works.length - this.works.length);
      this.works = works;
      this.isLoading = false;
      this.loadingMore = false;
      this.notifyNavigationContextUpdated();
      this.cdr.markForCheck();
    }
    return true;
  }

  private mergeHydratedWork(work: WorksModel): void {
    const works = applyWorkUpdate(
      this.works,
      {
        work,
        type: work.type,
        operator: "local",
      },
      {catalog: true}
    );
    if (works !== this.works) {
      this.works = works;
      this.cdr.markForCheck();
    }
  }

  private notifyNavigationContextUpdated(): void {
    if (
      this.workModalNavigationService.currentContext ===
      this.modalNavigationContext
    ) {
      this.workModalNavigationService.notifyContextUpdated();
    }
  }

  private restoreNavigationContext(): void {
    if (
      this.workModalNavigationService.currentContext !==
      this.modalNavigationContext
    ) {
      return;
    }
    if (this.previousNavigationContext) {
      this.workModalNavigationService.setContext(
        this.previousNavigationContext
      );
    } else {
      this.workModalNavigationService.clearContext(this.modalNavigationContext);
    }
    this.previousNavigationContext = null;
  }

  ngOnDestroy(): void {
    this.headerObserver?.disconnect();
    this.cancelSearch();
    this.restoreNavigationContext();
    this.hydrationSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
    this.inputTerm$.complete();
  }
}
